/*----

  todo: integrate combobox on pdp with global combobox script


  This JS is taken from this article - https://www.24a11y.com/2019/select-your-poison-part-2/
  It is also modified to be used as a way to decorate select dropdowns and make them
  accessible using keyboards.

  How to use it:
  Add js-combobox class to the <select> element you would like to replace with the combobox.
  Hide your <select> element as well as corresponding <label> element with display: none;.

  On load, this libary will create a new <label> element and a combobox element which consists
  of an input element and a listbox.

  The new <label> element will have select label's aria-label.
  The new combobox element will have select options' values and text.

  On option select, this libary will also set your hidden <select> element's val so it
  can be used further.

----*/
export class ComboBox {
  constructor() {
    this.keys = {
      Backspace: "Backspace",
      Clear: "Clear",
      Down: "ArrowDown",
      End: "End",
      Enter: "Enter",
      Escape: "Escape",
      Home: "Home",
      Left: "ArrowLeft",
      PageDown: "PageDown",
      PageUp: "PageUp",
      Right: "ArrowRight",
      Space: " ",
      Tab: "Tab",
      Up: "ArrowUp",
    };
    this.menuActions = {
      Close: 0,
      CloseSelect: 1,
      First: 2,
      Last: 3,
      Next: 4,
      Open: 5,
      Previous: 6,
      Select: 7,
      Space: 8,
      Type: 9,
    };

    this.el = false;
    this.selectEl = false;
    this.comboEl = false;
    this.inputEl = false;
    this.listboxEl = false;
    this.idBase = false;
    this.options = false;
    this.optionValues = false;
    this.activeIndex = 1;
    this.open = false;
  }

  // return an array of exact option name matches from a comma-separated string
  _findMatches = (options, search) => {
    const names = search.split(",");
    return names
      .map((name) => {
        const match = options.filter(
          (option) => name.trim().toLowerCase() === option.toLowerCase()
        );
        return match.length > 0 ? match[0] : null;
      })
      .filter((option) => option !== null);
  };

  // return combobox action from key press
  _getActionFromKey = (key, menuOpen) => {
    // handle opening when closed
    if (!menuOpen && key === Keys.Down) {
      return MenuActions.Open;
    }

    // handle keys when open
    if (key === Keys.Down) {
      return MenuActions.Next;
    } else if (key === Keys.Up) {
      return MenuActions.Previous;
    } else if (key === Keys.Home) {
      return MenuActions.First;
    } else if (key === Keys.End) {
      return MenuActions.Last;
    } else if (key === Keys.Escape) {
      return MenuActions.Close;
    } else if (key === Keys.Enter) {
      return MenuActions.CloseSelect;
    } else if (
      key === Keys.Backspace ||
      key === Keys.Clear ||
      key.length === 1
    ) {
      return MenuActions.Type;
    }
  };

  // get index of option that matches a string
  _getIndexByLetter = (options, filter) => {
    const firstMatch = this._filterOptions(options, filter)[0];
    return firstMatch ? options.indexOf(firstMatch) : -1;
  };

  // get updated option index
  _getUpdatedIndex = (current, max, action) => {
    switch (action) {
      case MenuActions.First:
        return 0;
      case MenuActions.Last:
        return max;
      case MenuActions.Previous:
        return Math.max(0, current - 1);
      case MenuActions.Next:
        return Math.min(max, current + 1);
      default:
        return current;
    }
  };

  // check if an element is currently scrollable
  _isScrollable = (element) => {
    return element && element.clientHeight < element.scrollHeight;
  };

  // ensure given child element is within the parent's visible scroll area
  _maintainScrollVisibility = (activeElement, scrollParent) => {
    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

    if (isAbove) {
      scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
      scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
  };

  // filter an array of options against an input string
  // returns an array of options that begin with the filter string, case-independent
  _filterOptions = (options = [], filter, exclude = []) => {
    return options.filter((option) => {
      const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
      return matches && exclude.indexOf(option) < 0;
    });
  };

  _init = () => {
    const container = $(".js-combobox");
    const $this = $(container);
    const labelEl = $(container).parents(
      `label[for="rtx_option_plans_dropdown"]`
    );

    // Insert combobox after select element's label
    labelEl.after(
      '<label for="combo' +
        this.index +
        '" aria-label="' +
        labelEl.attr("aria-label") +
        '" class="combo-label"></label>' +
        '<div class="combo js-aria11">' +
        '<div role="combobox" aria-haspopup="listbox" aria-expanded="false" aria-owns="listbox' +
        this.index +
        '" class="input-wrapper">' +
        '<input aria-activedescendant="" aria-autocomplete="none" id="combo' +
        this.index +
        '" class="combo-input" readonly type="text">' +
        '<div class="combo-menu" role="listbox" id="listbox' +
        this.index +
        '"></div>' +
        "</div>" +
        "</div>"
    );

    const selectOptions = $this.find("option");
    this.optionValues = $.map(selectOptions, (e) => $(e).val());
    this.options = $.map(selectOptions, (e) => e.label);

    const el = labelEl.siblings(".js-aria11")[0];

    // // init combo
    this.el = el;
    if (this.el && this.options.length) {
      this.selectEl = $this;
      this.comboEl = el.querySelector("[role=combobox]");
      this.inputEl = el.querySelector("input");
      this.listboxEl = el.querySelector("[role=listbox]");
      this.idBase = this.inputEl.id;

      this._buildCombo();

      // Add event listener for clicks outside the combo box
      document.addEventListener("click", this._onDocumentClick);
    } else {
      console.log("No Subscription Option for Product.");
    }
  };

  _buildCombo = () => {
    this.inputEl.addEventListener("input", this._onInput);
    this.inputEl.addEventListener("blur", this._onInputBlur);
    this.inputEl.addEventListener("click", () => this._updateMenuState(true));
    this.inputEl.addEventListener("keydown", this._onInputKeyDown);

    this.options.map((option, index) => {
      const optionEl = document.createElement("div");
      optionEl.setAttribute("role", "option");
      optionEl.id = `${this.idBase}-${index}`;
      optionEl.className =
        index === 0 ? "combo-option option-current" : "combo-option";
      optionEl.setAttribute("aria-selected", "false");
      optionEl.innerText = option;

      optionEl.addEventListener("click", () => {
        this._onOptionClick(index);
      });
      optionEl.addEventListener("mousedown", this._onOptionMouseDown());

      this.listboxEl.appendChild(optionEl);
    });

    this._onOptionClick(this.activeIndex);
  };

  _onInput = () => {
    const curValue = this.inputEl.value;
    const matches = this._filterOptions(this.options, curValue);

    // set activeIndex to first matching option
    // (or leave it alone, if the active option is already in the matching set)
    const filterCurrentOption = matches.filter(
      (option) => option === this.options[this.activeIndex]
    );
    if (matches.length > 0 && !filterCurrentOption.length) {
      this._onOptionChange(this.options.indexOf(matches[0]));
    }

    const menuState = this.options.length > 0;
    if (this.open !== menuState) {
      this._updateMenuState(menuState, false);
    }
  };

  _onInputKeyDown = function (event) {
    const { key } = event;
    const max = this.options.length - 1;

    const action = this._getActionFromKey(key, this.open);

    switch (action) {
      case MenuActions.Next:
      case MenuActions.Last:
      case MenuActions.First:
      case MenuActions.Previous:
        event.preventDefault();
        return this._onOptionChange(
          this._getUpdatedIndex(this.activeIndex, max, action)
        );
      case MenuActions.CloseSelect:
        event.preventDefault();
        this._selectOption(this.activeIndex);
        return this._updateMenuState(false);
      case MenuActions.Close:
        event.preventDefault();
        return this._updateMenuState(false);
      case MenuActions.Type:
        this._onOptionChange(Math.max(0, getIndexByLetter(this.options, key)));
      case MenuActions.Open:
        return this._updateMenuState(true);
    }
  };

  _onInputBlur = function () {
    if (this.ignoreBlur) {
      this.ignoreBlur = false;
      return;
    }

    if (this.open) {
      this._updateMenuState(false, false);
    }
  };

  _onOptionChange = function (index) {
    this.activeIndex = index;
    this.inputEl.setAttribute(
      "aria-activedescendant",
      `${this.idBase}-${index}`
    );

    // update active style
    const options = this.el.querySelectorAll("[role=option]");
    [...options].forEach((optionEl) => {
      optionEl.classList.remove("option-current");
    });
    options[index].classList.add("option-current");

    if (this.open && this._isScrollable(this.listboxEl)) {
      this._maintainScrollVisibility(options[index], this.listboxEl);
    }
  };

  _onOptionClick = function (index) {
    this._onOptionChange(index);
    this._selectOption(index);
    this._updateMenuState(false);
  };

  _onOptionMouseDown = function () {
    this.ignoreBlur = true;
  };

  _selectOption = function (index) {
    const selected = this.options[index];
    this.inputEl.value = selected;
    this.activeIndex = index;

    // update aria-selected
    const options = this.el.querySelectorAll("[role=option]");
    [...options].forEach((optionEl) => {
      optionEl.setAttribute("aria-selected", "false");
    });
    options[index].setAttribute("aria-selected", "true");

    // Update hidden select element's value
    this.selectEl.val(this.optionValues[index]);
    setSellingPlan(this.selectEl.data("product_id"));
    var dropdown = document.querySelector(".combo-input");
    var retexBuyBox = document.querySelector(".rtx_container");
    var currentProductId = retexBuyBox.id;
    dropdown.addEventListener("click", function (e) {
      const noClickBtn = document.getElementById(
        `product_onetime_${currentProductId}`
      );
      noClickBtn.checked = false;
      const yesClickBtn = document.getElementById(
        `product_subsave_${currentProductId}_1`
      );
      yesClickBtn.checked = true;
    });
  };

  _updateMenuState = function (open, callFocus = true) {
    this.open = open;

    this.comboEl.setAttribute("aria-expanded", `${open}`);
    open ? this.el.classList.add("open") : this.el.classList.remove("open");
  };

  _onDocumentClick = (event) => {
    if (this.open && !this.el.contains(event.target)) {
      this._updateMenuState(false);
    }
  };
}